import { useState } from 'react';
import { type DietaryRestrictionsText } from '@hungryroot/dietary-restrictions';
import { type Cart, type PairingV1 as Pairing } from '@hungryroot/rest';
import {
  AddRemoveBtn,
  Variant as AddRemoveButtonVariant,
  VisualVariant as AddRemoveButtonVisualVariant,
} from '../../../pages/customize/components/CartAddRemoveButton/AddRemoveButton';
import { getCartPairingBundle, getPairingCount } from '../../cart/query';
import { convertPairingToCartPairing } from '../../cart/transforms';
import { useCustomization } from '../../CustomizationProvider/CustomizationProvider';
interface Props {
  pairing: Pairing;
  closeModal: () => void;
  modified: boolean;
  points?: string;
  onAddClick?: () => void;
  onPairingAdded?: () => void;
  onPairingRemoved?: () => void;
  onUpdateClick?: (cart: Cart) => void;
  dietaryIngredients?: DietaryRestrictionsText[];
  isLoading?: boolean;
}

function getPairingPoints(props: Props, initialPairing: Pairing | undefined) {
  const { points, pairing } = props;
  if (points !== undefined) {
    return points;
  }
  const { id: pairingId } = pairing;
  const pairingPoints =
    initialPairing && initialPairing.id === pairingId
      ? pairing.ingredients
          .reduce(function (acc, obj) {
            const { points, amount } = obj;
            let newAcc = acc;

            for (let i = 0; i < amount; i += 1) {
              newAcc += points;
            }
            return newAcc;
          }, 0)
          .toString()
      : pairing.points.toString();

  return pairingPoints;
}

export function RDPAddRemoveBtn(props: Props) {
  const {
    pairing,
    closeModal,
    modified,
    onAddClick,
    onUpdateClick,
    onPairingAdded,
    onPairingRemoved,
    dietaryIngredients,
    isLoading,
  } = props;
  const [initialPairing, setInitialPairing] = useState<Pairing | undefined>(
    undefined
  );

  const customization = useCustomization();

  if (initialPairing === undefined) {
    setInitialPairing(pairing);
  }

  if (!('cart' in customization) || isLoading) {
    return null;
  }

  const {
    pairingCanBeAdded,
    cart,
    mutations: { removeRecipeFromBundle: removeRecipe, addPairing, swapRecipe },
  } = customization;

  const { id: pairingId } = pairing;
  const pairingCount = getPairingCount(cart, pairingId);
  const variant =
    initialPairing &&
    getPairingCount(cart, initialPairing.id) > 0 &&
    (initialPairing.id !== pairingId || modified)
      ? AddRemoveButtonVariant.Update
      : AddRemoveButtonVariant.AddRemove;

  const pairingPoints = getPairingPoints(props, initialPairing);

  const handleOnAddClick = () => {
    if (onAddClick !== undefined) {
      onAddClick();
      return;
    }
    addPairing({
      pairing,
      dietaryProductIngredients: dietaryIngredients,
      location: 'rdp',
    });
    closeModal?.();
  };

  const handleOnClick = () => {
    handleOnAddClick();
    onPairingAdded && onPairingAdded();
  };

  if (variant === AddRemoveButtonVariant.Update) {
    const updateInitialPairing = initialPairing as Pairing;
    return (
      <AddRemoveBtn
        useCartResult={customization}
        swapRecipe={{
          count: getPairingCount(cart, updateInitialPairing.id),
          pairing: initialPairing as Props['pairing'],
        }}
        variant={variant}
        count={getPairingCount(cart, updateInitialPairing.id)}
        pairingPoints={pairingPoints}
        item={{
          id: pairing.id,
          points: pairingPoints,
        }}
        visualVariant={AddRemoveButtonVisualVariant.Light}
        onClick={handleOnClick}
        onUpdateClick={() => {
          if (onUpdateClick !== undefined) {
            onUpdateClick(cart);
            return;
          }
          const { id: bundleId } = getCartPairingBundle(
            cart,
            updateInitialPairing.id
          );

          swapRecipe({
            bundleId,
            recipe: convertPairingToCartPairing(pairing),
          });
          closeModal();
        }}
      />
    );
  }

  return (
    <AddRemoveBtn
      useCartResult={customization}
      disabled={!pairingCanBeAdded(pairing)}
      visualVariant={AddRemoveButtonVisualVariant.Light}
      variant={variant}
      count={pairingCount}
      pairingPoints={pairingPoints}
      item={{
        id: pairing.id,
        points: pairingPoints,
      }}
      onRemoveClick={() => {
        const { id: bundleId } = getCartPairingBundle(cart, pairingId);
        removeRecipe({
          bundleId,
          recipeId: pairingId,
          pairingName: pairing.name,
        });
        closeModal();
      }}
      onClick={handleOnClick}
      onAddClick={handleOnAddClick}
      onPairingAdded={onPairingAdded}
      onPairingRemoved={onPairingRemoved}
    />
  );
}
